import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { VariantPicker, type Variant as Pickable } from '../../components/pickers';
import { PageHeader } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { catalogKeys, type Product, type Variant } from './model';
import { useProduct } from './queries';
import { SectionTitle } from './ui';

interface Line {
  key: string;
  component: { id: string; name: string; productId: string } | null;
  unitId: string;
  qty: string;
}

interface Picked {
  id: string;
  displayName: string;
  productId: string;
  baseUnit: { id: string };
}

let lineSeq = 0;
const blankLine = (): Line => ({
  key: `l${String(++lineSeq)}`,
  component: null,
  unitId: '',
  qty: '',
});

/**
 * /bom/new and /bom/:productId/:variantId — a bill of materials like Odoo's: the product it makes,
 * whether that product tracks its own stock, whether returns give stock back, and the components
 * one unit uses.
 */
export function BomFormPage() {
  const { productId = '', variantId = '' } = useParams();
  const product = useProduct(productId);
  const variant = product.data?.variants.find((v) => v.id === variantId) ?? null;
  if (productId && (product.isLoading || !product.data)) {
    return (
      <Card sx={{ p: 3 }}>
        {product.isError ? (
          <Alert severity="error">This product could not be loaded.</Alert>
        ) : (
          <Skeleton height={160} />
        )}
      </Card>
    );
  }
  // Remount when the product changes so the form starts from its saved bill of materials.
  return (
    <BomForm
      key={`${productId}:${variantId}`}
      initial={product.data && variant ? { product: product.data, variant } : null}
    />
  );
}

function BomForm({ initial }: { initial: { product: Product; variant: Variant } | null }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canUpdate = can('products.update');

  const [picked, setPicked] = useState<{
    productId: string;
    variantId: string;
    name: string;
  } | null>(
    initial
      ? {
          productId: initial.product.id,
          variantId: initial.variant.id,
          name: initial.variant.displayName,
        }
      : null,
  );
  // The chosen product, read fresh: its type, Track stock, unit and the variant's version.
  const target = useProduct(picked?.productId ?? '');
  const p = target.data ?? initial?.product ?? null;
  const v = p?.variants.find((x) => x.id === picked?.variantId) ?? null;
  const existing = v?.recipe ?? null;

  const [trackInventory, setTrackInventory] = useState<boolean | null>(null);
  const [returnsToStock, setReturnsToStock] = useState(
    initial?.variant.recipe?.returnsToStock ?? true,
  );
  const [lines, setLines] = useState<Line[]>(() =>
    initial?.variant.recipe
      ? initial.variant.recipe.lines.map((l) => ({
          key: `l${String(++lineSeq)}`,
          component: { id: l.variantId, name: l.name, productId: l.productId },
          unitId: l.unitId,
          qty: l.qty,
        }))
      : [blankLine()],
  );
  const [error, setError] = useState<string | null>(null);
  const tracking = trackInventory ?? p?.trackInventory ?? false;
  const notGoods = p !== null && p.productType !== 'GOODS';

  const update = (key: string, change: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...change } : l)));

  const save = useMutation({
    mutationFn: (filled: Line[]) =>
      unwrap(
        api.PUT('/variants/{id}/recipe', {
          params: { path: { id: v?.id ?? '' } },
          body: {
            returnsToStock,
            ...(p && tracking !== p.trackInventory ? { trackInventory: tracking } : {}),
            lines: filled.map((l) => ({
              variantId: l.component?.id ?? '',
              ...(l.unitId ? { unitId: l.unitId } : {}),
              qty: l.qty.trim(),
            })),
            version: v?.version ?? 0,
          },
        }),
      ),
    onSuccess: async (_, filled) => {
      notify.success(
        filled.length
          ? `Bill of materials of ${v?.displayName ?? ''} saved`
          : `Bill of materials of ${v?.displayName ?? ''} removed`,
      );
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      void navigate('/bom');
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const submit = (remove = false) => {
    if (!v) {
      setError('Pick the product this bill of materials makes.');
      return;
    }
    const filled = remove ? [] : lines.filter((l) => l.component || l.qty.trim());
    const problem = remove
      ? null
      : filled.length === 0
        ? 'Add at least one component.'
        : filled.some((l) => !l.component)
          ? 'Pick a component on every line.'
          : filled.some((l) => !(Number(l.qty) > 0))
            ? 'Enter a quantity above zero on every line.'
            : new Set(filled.map((l) => l.component?.id)).size !== filled.length
              ? 'Each component can be listed once.'
              : filled.some((l) => l.component?.id === v.id)
                ? 'A product cannot be a component of itself.'
                : null;
    setError(problem);
    if (!problem) save.mutate(filled);
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Button startIcon={<ArrowBackRounded />} onClick={() => void navigate('/bom')}>
          Bills of materials
        </Button>
      </Box>
      <PageHeader
        title={existing ? `Bill of materials: ${v?.displayName ?? ''}` : 'New bill of materials'}
        subtitle="The components one unit of a product uses. Selling the product takes them from stock."
      />
      {error && <Alert severity="error">{error}</Alert>}

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <SectionTitle
            title="Product"
            hint="An existing goods product, made from the components below."
          />
          <Box sx={{ maxWidth: 560 }}>
            <VariantPicker
              label="Product"
              value={
                picked
                  ? ({
                      id: picked.variantId,
                      displayName: picked.name,
                      sku: '',
                    } as unknown as Pickable)
                  : null
              }
              onChange={(x) => {
                const s = x as unknown as Picked | null;
                setPicked(
                  s ? { productId: s.productId, variantId: s.id, name: s.displayName } : null,
                );
                setTrackInventory(null);
                setError(null);
              }}
            />
          </Box>
          {notGoods && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              {p.name} is a {p.productType === 'COMBO' ? 'combo' : 'service'}. Only goods have a
              bill of materials.
            </Alert>
          )}
          {v && p && !notGoods && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Per 1 {p.baseUnit.symbol} of {v.displayName}.
            </Typography>
          )}
          {existing && !initial && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void navigate(`/bom/${p?.id ?? ''}/${v?.id ?? ''}`)}
                >
                  Open it
                </Button>
              }
            >
              {v?.displayName} already has a bill of materials.
            </Alert>
          )}

          <Stack spacing={1.5} sx={{ mt: 2.5 }}>
            <FormControlLabel
              disabled={!p || notGoods}
              control={
                <Switch checked={tracking} onChange={(e) => setTrackInventory(e.target.checked)} />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Track stock
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {tracking
                      ? 'On: the product keeps its own stock, movements and value. Each sale first makes what is not in stock from the components.'
                      : 'Off: the product has no stock of its own. Each sale takes the components from stock directly.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Can change only before the product has stock history or documents.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              disabled={!p || notGoods}
              control={
                <Switch
                  checked={returnsToStock}
                  onChange={(e) => setReturnsToStock(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Return to inventory
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {returnsToStock
                      ? tracking
                        ? 'On: a returned product goes back into its own stock.'
                        : 'On: a returned product gives its components back to stock.'
                      : 'Off: nothing comes back to stock on a return; it counts as used.'}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <SectionTitle
            title="Components"
            hint={`What 1 ${p?.baseUnit.symbol ?? 'unit'} of the product uses. Components are goods that track stock.`}
          />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Component</TableCell>
                  <TableCell sx={{ width: 140 }}>Quantity</TableCell>
                  <TableCell sx={{ width: 130 }}>Unit</TableCell>
                  <TableCell sx={{ width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, i) => (
                  <ComponentLine
                    key={l.key}
                    index={i}
                    line={l}
                    onChange={(change) => update(l.key, change)}
                    onRemove={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <Button
            startIcon={<AddRounded />}
            sx={{ mt: 1.5 }}
            disabled={lines.length >= 50}
            onClick={() => setLines((ls) => [...ls, blankLine()])}
          >
            Add a line
          </Button>
        </CardContent>
      </Card>

      {canUpdate && (
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
          {existing && initial && (
            <Button color="error" disabled={save.isPending} onClick={() => submit(true)}>
              Remove bill of materials
            </Button>
          )}
          <Button onClick={() => void navigate('/bom')}>Cancel</Button>
          <Button
            variant="contained"
            disabled={save.isPending || !v || notGoods || (existing !== null && !initial)}
            onClick={() => submit()}
          >
            Save
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function ComponentLine({
  index,
  line,
  onChange,
  onRemove,
}: {
  index: number;
  line: Line;
  onChange: (change: Partial<Line>) => void;
  onRemove: () => void;
}) {
  // Units come from the component's own product: its base unit and pack sizes.
  const component = useProduct(line.component?.productId ?? '');
  const c = component.data;
  const units = c
    ? [
        { unitId: c.baseUnit.id, symbol: c.baseUnit.symbol },
        ...c.units.map((u) => ({ unitId: u.unitId, symbol: u.symbol })),
      ]
    : [];
  const n = String(index + 1);
  return (
    <TableRow sx={{ '& td': { verticalAlign: 'top', py: 1.25 } }}>
      <TableCell>
        <VariantPicker
          label={`Component ${n}`}
          value={
            line.component
              ? ({
                  id: line.component.id,
                  displayName: line.component.name,
                  sku: '',
                } as unknown as Pickable)
              : null
          }
          onChange={(x) => {
            const s = x as unknown as Picked | null;
            onChange(
              s
                ? {
                    component: { id: s.id, name: s.displayName, productId: s.productId },
                    unitId: s.baseUnit.id,
                  }
                : { component: null, unitId: '' },
            );
          }}
        />
        {c && (!c.trackInventory || c.productType !== 'GOODS') && (
          <Typography variant="caption" color="error">
            Doesn’t track stock, so it can’t be a component.
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          value={line.qty}
          onChange={(e) => onChange({ qty: e.target.value })}
          slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': `Quantity ${n}` } }}
        />
      </TableCell>
      <TableCell>
        <TextField
          select
          size="small"
          fullWidth
          value={units.some((u) => u.unitId === line.unitId) ? line.unitId : ''}
          onChange={(e) => onChange({ unitId: e.target.value })}
          disabled={units.length === 0}
          slotProps={{ htmlInput: { 'aria-label': `Unit ${n}` } }}
        >
          {units.map((u) => (
            <MenuItem key={u.unitId} value={u.unitId}>
              {u.symbol}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <Tooltip title="Remove line">
          <IconButton aria-label={`Remove line ${n}`} onClick={onRemove}>
            <DeleteOutlineRounded />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
